import React, { useState, useCallback, useEffect, useContext } from "react";
import { MapView } from "../../views/MapView";
import { MapService } from "../../services/mapService";
import { useMapSync } from "../../hooks/useMapSync";
import { usePreferences } from "../../hooks/usePreferences";
import { getMapDataSource } from "../../services/mapDataSource";
import { getSimulationService } from "../../services/simulationService";
import { ActivityContext } from "../../contexts/ActivityContext";
import { getActivityStorageService } from "../../services/activityStorageService";
import { friendsService } from "../../services/friendsService";
import { toLonLat } from "ol/proj";
import debugx from "debugx";

const debug = debugx("bikedash:mapsync");

/**
 * Wrapper component that handles syncing map preferences to the MapService
 * This keeps the MapView pure and prevents re-renders
 */
export function MapSyncWrapper() {
  const [mapService, setMapService] = useState<MapService | null>(null);
  const { mapPreferences, userPreferences, updateMapPreferences } =
    usePreferences();
  const activityContext = useContext(ActivityContext);

  // Sync map preferences to the map service
  useMapSync(mapPreferences, mapService, userPreferences.apiKeys);

  // Watch for debug overlay changes
  useEffect(() => {
    if (!mapService) return;

    const checkDebugOverlay = () => {
      const isShowing = localStorage.getItem("showDebugOverlay") === "true";
      mapService.setDebugOverlayShowing(isShowing);
    };

    // Check initial state
    checkDebugOverlay();

    // Listen for changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "showDebugOverlay") {
        checkDebugOverlay();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Also listen for custom events from same window
    const handleDebugToggle = () => checkDebugOverlay();
    window.addEventListener("debugOverlayToggled", handleDebugToggle);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("debugOverlayToggled", handleDebugToggle);
    };
  }, [mapService]);

  // Subscribe to interpolated positions from mapDataSource
  useEffect(() => {
    if (!mapService) return;

    const mapDataSource = getMapDataSource();
    let lastPositionUpdate = 0;

    const subscription = mapDataSource.subscribe((data) => {
      if (data.currentPosition) {
        const now = Date.now();

        // Check if this looks like a position update (happens ~1fps)
        // vs a compass update (happens at 60fps)
        const isPositionUpdate =
          data.currentPosition.lat &&
          data.currentPosition.lng &&
          (data.currentPosition.isInterpolated ||
            now - lastPositionUpdate > 100); // Position updates are slower

        if (isPositionUpdate) {
          // Full position update
          lastPositionUpdate = now;
          mapService.updatePosition(data.currentPosition);
        }

        // Update heading based on user preference
        const useGpsHeading = mapPreferences.useGpsHeading ?? false;

        if (useGpsHeading) {
          // Use GPS-derived heading (from movement)
          if (data.currentPosition.heading !== undefined) {
            mapService.updatePositionHeading(data.currentPosition.heading);
          }
        } else {
          // Use compass heading (magnetic)
          if (data.currentPosition.compassHeading !== undefined) {
            mapService.updatePositionHeading(
              data.currentPosition.compassHeading,
            );
          }
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [mapService, mapPreferences.useGpsHeading]);

  // Immediately update heading when useGpsHeading preference changes
  useEffect(() => {
    if (!mapService) return;

    const mapDataSource = getMapDataSource();
    const currentPosition = mapDataSource.currentPosition;

    if (currentPosition) {
      const useGpsHeading = mapPreferences.useGpsHeading ?? false;

      if (useGpsHeading && currentPosition.heading !== undefined) {
        mapService.updatePositionHeading(currentPosition.heading);
      } else if (
        !useGpsHeading &&
        currentPosition.compassHeading !== undefined
      ) {
        mapService.updatePositionHeading(currentPosition.compassHeading);
      }
    }
  }, [mapService, mapPreferences.useGpsHeading]);

  // Subscribe to simulation route changes
  useEffect(() => {
    if (!mapService) return;

    const simulationService = getSimulationService();

    // Check periodically for route and option changes
    const interval = setInterval(() => {
      const state = simulationService.getState();
      const options = simulationService.getOptions();
      const currentRoute = simulationService.getCurrentRoute();

      if (state.isActive && currentRoute && options.showRouteOnMap) {
        // Show the route on the map
        mapService.setRoute(currentRoute);
      } else if (!options.showRouteOnMap || !state.isActive) {
        // Clear the route from the map by clearing the source
        mapService.clearRoute();
      }
    }, 500); // Check every 500ms

    return () => clearInterval(interval);
  }, [mapService]);

  // Show active/paused activity track on the map
  useEffect(() => {
    if (!mapService || !activityContext) return;

    let intervalId: NodeJS.Timeout;
    let lastTrackPointCount = 0;

    const updateActivityTrack = async () => {
      try {
        const { state, currentActivityId } = activityContext;

        // Only show track if activity is active or paused
        if (
          (state === "recording" || state === "paused") &&
          currentActivityId
        ) {
          const storageService = getActivityStorageService();
          const trackPoints =
            await storageService.loadTrackPoints(currentActivityId);

          // Only update if we have new points
          if (trackPoints.length !== lastTrackPointCount) {
            lastTrackPointCount = trackPoints.length;

            // Convert track points to format for activity track display
            if (trackPoints.length > 1) {
              const trackData = trackPoints
                .filter(
                  (tp) =>
                    tp.position.lat !== undefined &&
                    tp.position.lng !== undefined,
                )
                .map((tp) => ({
                  lat: tp.position.lat!,
                  lng: tp.position.lng!,
                  timestamp: tp.timestamp,
                }));

              debug(
                "Updating activity track on map with %d points",
                trackData.length,
              );

              // Use the new activity track method which handles gaps and uses track styles
              mapService.setActivityTrack(trackData);
            }
          }
        } else {
          // Clear the activity track when activity stops
          if (lastTrackPointCount > 0) {
            mapService.clearActivityTrack();
            lastTrackPointCount = 0;
            debug("Cleared activity track from map");
          }
        }
      } catch (error) {
        debug.error("Failed to update activity track:", error);
      }
    };

    // Initial update
    updateActivityTrack();

    // Update every 2 seconds to catch new track points
    intervalId = setInterval(updateActivityTrack, 2000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      // Clear activity track when unmounting
      mapService.clearActivityTrack();
    };
  }, [mapService, activityContext?.state, activityContext?.currentActivityId]);

  const handleMapReady = useCallback(
    (service: MapService) => {
      setMapService(service);

      // Listen for zoom changes from the map
      const view = service.getView();
      if (view) {
        let zoomUpdateTimeout: NodeJS.Timeout | undefined;

        view.on("change:resolution", () => {
          const zoom = view.getZoom();
          if (zoom !== undefined && zoom !== mapPreferences.zoomLevel) {
            // Debounce zoom updates to prevent feedback loops during pinch zoom
            if (zoomUpdateTimeout) {
              clearTimeout(zoomUpdateTimeout);
            }

            zoomUpdateTimeout = setTimeout(() => {
              // Only update if zoom has actually changed and stabilized
              const currentZoom = view.getZoom();
              if (
                currentZoom !== undefined &&
                currentZoom !== mapPreferences.zoomLevel
              ) {
                updateMapPreferences({ zoomLevel: currentZoom });
              }
            }, 300); // Wait for zoom to stabilize
          }
        });

        // Periodically save the map center
        let lastSavedCenter: number[] | null = null;
        const saveCenter = () => {
          const center = view.getCenter();
          if (center) {
            // Convert from map projection to lng/lat
            const lonLat = toLonLat(center);
            // Only save if center has changed significantly (more than ~10 meters)
            if (
              !lastSavedCenter ||
              Math.abs(lonLat[0] - lastSavedCenter[0]) > 0.0001 ||
              Math.abs(lonLat[1] - lastSavedCenter[1]) > 0.0001
            ) {
              lastSavedCenter = lonLat;
              updateMapPreferences({ lastCenter: [lonLat[0], lonLat[1]] });
            }
          }
        };

        // Save center on view change
        view.on("change:center", saveCenter);

        // Also save periodically in case of continuous movement
        setInterval(saveCenter, 5000);
      }

      return undefined;
    },
    [mapPreferences.zoomLevel, updateMapPreferences],
  );

  // Subscribe to friend location updates
  useEffect(() => {
    if (!mapService) return;

    debug("Subscribing to friend location updates");

    // Helper function to update friend markers on the map
    const updateFriendMarkers = (
      locations: { [friendId: string]: any },
      friends: any[],
    ) => {
      debug("🗺️ updateFriendMarkers called");
      debug("  Location count: %d", Object.keys(locations).length);
      debug("  Friends count: %d", friends.length);

      if (friends.length === 0) {
        debug("  ⚠️ WARNING: Friends list is empty! Cannot match locations.");
        debug("  This might mean friends haven't loaded yet.");
        return; // Don't call updateFriends with empty array
      }

      // Filter out stale locations (older than 20 seconds)
      const now = Date.now();
      const LOCATION_TIMEOUT = 20 * 1000; // 20 seconds
      const freshLocations: { [friendId: string]: any } = {};

      Object.entries(locations).forEach(([friendId, location]) => {
        const age = now - (location.timestamp || 0);
        if (age < LOCATION_TIMEOUT) {
          freshLocations[friendId] = location;
        } else {
          debug(
            "  ⏱️ Filtering out stale location for friend %s (age: %ds)",
            friendId,
            Math.floor(age / 1000),
          );
        }
      });

      debug(
        "  Fresh locations: %d / %d",
        Object.keys(freshLocations).length,
        Object.keys(locations).length,
      );
      debug(
        "Friend user IDs: %o",
        friends.map((f) => f.userId),
      );
      debug("Location keys: %o", Object.keys(freshLocations));

      // Convert friend locations to the format expected by updateFriends
      const friendsWithLocations = Object.entries(freshLocations)
        .map(([friendId, location]) => {
          debug("Processing location for friendId: %s", friendId);

          // The friendId from the server is the friend's user ID
          // Match against friend.id (which contains the user ID)
          const friend = friends.find((f) => f.id === friendId);
          if (!friend) {
            debug("❌ No friend found for id: %s", friendId);
            debug(
              "   Available friend IDs: %o",
              friends.map((f) => f.id),
            );
            return null;
          }

          debug("✅ Matched friend: %s (id: %s)", friend.username, friend.id);
          if (!location.lat || !location.lng) {
            debug("Invalid location for friend %s: %o", friendId, location);
            return null;
          }

          debug(
            "✅ Found friend %s (%s) at lat=%s, lng=%s",
            friend.username,
            friendId,
            location.lat,
            location.lng,
          );

          const position: any = {
            lat: location.lat,
            lng: location.lng,
            timestamp: location.timestamp || Date.now(),
          };

          if (location.heading !== undefined) {
            position.heading = location.heading;
          }
          if (location.speed !== undefined) {
            position.speed = location.speed;
          }

          return {
            friend,
            position,
          };
        })
        .filter(Boolean) as Array<{
        friend: any;
        position: any;
      }>;

      debug(
        "Updating map with %d friend locations",
        friendsWithLocations.length,
      );
      mapService.updateFriends(friendsWithLocations);
    };

    // Subscribe to location changes
    const unsubscribeLocations = friendsService.onFriendLocationsChange(
      (locations) => {
        debug("Friend locations updated: %o", locations);
        const friends = friendsService.getCachedFriends();
        updateFriendMarkers(locations, friends);
      },
    );

    // Also subscribe to friends list changes to re-process locations
    // when friends list updates (e.g., after initial load or icon/color changes)
    const unsubscribeFriends = friendsService.onFriendsChange((friends) => {
      debug("Friends list updated, re-processing locations");
      // Get current locations and update markers
      const locations = friendsService.getCachedFriendLocations();
      updateFriendMarkers(locations, friends);
    });

    // Periodically refresh friends list to get updated icon/color configs
    const refreshInterval = setInterval(() => {
      debug("Periodic friends list refresh for map icon/color updates");
      friendsService.getFriends().catch((error) => {
        debug.error("Failed to refresh friends list:", error);
      });
    }, 10000); // Every 10 seconds

    return () => {
      unsubscribeLocations();
      unsubscribeFriends();
      clearInterval(refreshInterval);
    };
  }, [mapService]);

  return (
    <MapView
      onMapReady={handleMapReady}
      initialZoom={mapPreferences.zoomLevel}
      initialMode={mapPreferences.mode}
      initialCenter={mapPreferences.lastCenter}
      initialStyleId={mapPreferences.defaultStyleId}
      apiKeys={userPreferences.apiKeys}
    />
  );
}
